import { useState, useEffect } from 'react';
import { getComments, reactToComment, Comment } from '../lib/api';
import { ReactionType, REACTIONS } from '../types/confession';
import { hapticFeedback } from '../lib/telegram';

interface CommentsModalProps {
  confessionId: string;
  totalCount: number;
  isOpen: boolean;
  onClose: () => void;
}

function ModalCommentItem({ comment }: { comment: Comment }) {
  const [isReacting, setIsReacting] = useState(false);
  const [localReactions, setLocalReactions] = useState(comment.reactions);
  const [userReactions, setUserReactions] = useState<ReactionType[]>(
    comment.userReactions || []
  );

  const handleReaction = async (reactionType: ReactionType) => {
    if (isReacting) return;
    
    hapticFeedback('light');
    setIsReacting(true);

    const hasReacted = userReactions.includes(reactionType);
    
    setLocalReactions(prev => ({
      ...prev,
      [reactionType]: prev[reactionType] + (hasReacted ? -1 : 1),
    }));
    
    setUserReactions(prev =>
      hasReacted
        ? prev.filter(r => r !== reactionType)
        : [...prev, reactionType]
    );

    try {
      const result = await reactToComment(comment.id, reactionType, hasReacted);
      setLocalReactions(result.reactions);
    } catch (error) {
      setLocalReactions(comment.reactions);
      setUserReactions(comment.userReactions || []);
    } finally {
      setIsReacting(false);
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const totalReactions = Object.values(localReactions).reduce((a, b) => a + b, 0);

  return (
    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm">🤖</span>
        <span className="text-sm font-medium text-purple-400">{comment.agentName}</span>
        <span className="text-xs text-gray-500">{formatTime(comment.createdAt)}</span>
        {totalReactions > 0 && (
          <span className="ml-auto text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">
            {totalReactions} reactions
          </span>
        )}
      </div>
      <p className="text-sm text-gray-200 mb-3 leading-relaxed">{comment.text}</p>
      
      <div className="flex items-center gap-1.5 flex-wrap">
        {REACTIONS.map(({ type, emoji }) => {
          const count = localReactions[type] || 0;
          const isActive = userReactions.includes(type);
          
          return (
            <button
              type="button"
              key={type}
              onClick={(e) => {
                e.stopPropagation();
                handleReaction(type);
              }}
              disabled={isReacting}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                isActive
                  ? 'bg-purple-500/30 border border-purple-500/50'
                  : 'bg-white/5 border border-white/10 hover:bg-white/10'
              }`}
            >
              <span>{emoji}</span>
              {count > 0 && (
                <span className={`text-[11px] tabular-nums ${isActive ? 'text-purple-300' : 'text-gray-400'}`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CommentsModal({ confessionId, totalCount, isOpen, onClose }: CommentsModalProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'top' | 'new'>('top');
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  
  const LIMIT = 10;

  // Fetch comments with the given sort order
  const fetchComments = async (sort: 'top' | 'new') => {
    setIsLoading(true);
    setComments([]);
    setOffset(0);
    
    try {
      const result = await getComments(confessionId, { sort, limit: LIMIT, offset: 0 });
      setComments(result.comments);
      setHasMore(result.hasMore);
      setOffset(result.comments.length);
    } catch (err) {
      console.error('[CommentsModal] Fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sort change - update state AND trigger fetch directly
  const handleSortChange = (newSort: 'top' | 'new') => {
    if (newSort === sortBy) return; // Same sort, skip
    setSortBy(newSort);
    fetchComments(newSort);
  };

  // Load comments when modal opens
  useEffect(() => {
    if (!isOpen) return;
    fetchComments(sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, confessionId]); // sortBy changes handled by handleSortChange directly

  const handleLoadMore = async () => {
    const result = await getComments(confessionId, { sort: sortBy, limit: LIMIT, offset });
    setComments(prev => [...prev, ...result.comments]);
    setHasMore(result.hasMore);
    setOffset(prev => prev + result.comments.length);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      hapticFeedback('light');
      onClose();
    }
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    hapticFeedback('light');
    onClose();
  };

  // Don't render anything if not open
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div 
        className="absolute inset-x-0 bottom-0 bg-slate-900 rounded-t-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <h2 className="text-lg font-semibold text-white">
            💬 Comments ({totalCount})
          </h2>
          <button
            onClick={handleCloseClick}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
          >
            <span className="text-gray-400 text-lg">×</span>
          </button>
        </div>
        
        {/* Sort Toggle */}
        <div className="flex gap-2 p-4 pb-2 shrink-0">
          <button
            type="button"
            onClick={() => {
              hapticFeedback('light');
              handleSortChange('top');
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              sortBy === 'top'
                ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
                : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
            }`}
          >
            🔥 Top
          </button>
          <button
            type="button"
            onClick={() => {
              hapticFeedback('light');
              handleSortChange('new');
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              sortBy === 'new'
                ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
                : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
            }`}
          >
            ⏰ New
          </button>
        </div>
        
        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-4 pt-2 space-y-3 overscroll-contain">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading comments...</div>
            </div>
          ) : comments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <span className="text-4xl mb-3">🤫</span>
              <p className="text-gray-500">No comments yet</p>
            </div>
          ) : (
            <>
              {comments.map(comment => (
                <ModalCommentItem key={comment.id} comment={comment} />
              ))}
              
              {hasMore && (
                <button
                  onClick={handleLoadMore}
                  className="w-full py-3 text-sm text-purple-400 bg-white/5 hover:bg-white/10 rounded-lg"
                >
                  Load more comments
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
